/**
 * Per-slot batched autosave for one pond/date of water readings (NULL ≠ 0).
 * POST creates the slot's row, PUT updates it. A hard-limit 400 or a field-naming 422
 * blocks only the columns it names; the slot's other fields are sent again without them
 * and still persist. A 400/422 that names no column cannot succeed on retry: every
 * column of that attempt is refused (shown "Not saved"), never left to look saved. A pair (alkalinity,
 * Cal/Mag) is one value to the user: when the server refuses one number, the other is withheld too, so a
 * refused pair saves nothing and never leaves a half-changed pair behind. A create whose
 * values are all blank is never sent (it would leave a row that reads as logged).
 *
 * `retry: 'auto'` (desktop grid) re-queues a failed save and retries on a timer.
 * `retry: 'manual'` (phone) re-queues it too, but marks the cells as failed and
 * waits for `retrySlot` (the "Try again" tap) or the next commit in that slot.
 * Once the screen has closed nobody can tap it, so a failure then falls back to
 * the timed retry with a toast.
 *
 * Failed attempts back off (1 s, 2 s, 5 s, then every 10 s) until one succeeds,
 * and share one toast. A 401 or 403 is not retried: retrying only repeats the
 * failure; the user is told once (signed out / not allowed). A token refresh that
 * failed reaches here as the same 401 (api.ts), and `signedOut` lets the screen offer
 * signing in again.
 *
 * `savedCount` counts confirmed saves in this session (0 = nothing has landed
 * yet). `onRejected` fires when the server refuses columns, with the values
 * that were refused.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { api } from '@/lib/api';
import {
  ApiError,
  getHardLimitErrorFields,
  getValidationErrorFields,
  registerAutosaveFlusher,
} from '@/lib/apiErrors';
import { ALL_SLOTS, dbColumnFor, pairPartnerColumn, toWirePayload } from '@/lib/waterModel';
import type { ReadingSlot } from '@/types/entry';
import type { DayReadings } from './entryData';

export type CellVal = number | string | null;

export const cellKey = (slot: ReadingSlot, col: string) => `${slot}|${col}`;

const isBlankVal = (v: CellVal | undefined) => v === null || v === undefined || v === '';

const RETRY_DELAYS_MS = [1000, 2000, 5000, 10000];
const RESAVE_DELAY_MS = 350;
const RETRY_TOAST_ID = 'autosave-retry';
const SIGNED_OUT_TOAST_ID = 'autosave-signed-out';
const FORBIDDEN_TOAST_ID = 'autosave-forbidden';
// Longer than the slowest retry, so the one toast stays up for as long as saves keep failing.
const RETRY_TOAST_MS = 12000;
const SIGNED_OUT_TOAST_MS = 15000;
const isSignedOut = (err: unknown) => err instanceof ApiError && err.status === 401;
const isForbidden = (err: unknown) => err instanceof ApiError && err.status === 403;

interface Options {
  farmId: string;
  cycleId: string;
  date: string;
  dayReadings: DayReadings;
  onSaved: () => void;
  retry?: 'auto' | 'manual';
  onRejected?: (slot: ReadingSlot, columns: string[], values: Record<string, CellVal>) => void;
}

export function useReadingAutosave({ farmId, cycleId, date, dayReadings, onSaved, retry = 'auto', onRejected }: Options) {
  const [edits, setEdits] = useState<Record<string, CellVal>>({});
  const [rejected, setRejected] = useState<Set<string>>(new Set());
  const [failed, setFailed] = useState<Set<string>>(new Set());
  const [slotSaving, setSlotSaving] = useState<Record<string, boolean>>({});
  const [slotQueued, setSlotQueued] = useState<Record<string, boolean>>({});
  const [savedCount, setSavedCount] = useState(0);
  const [signedOut, setSignedOut] = useState(false);

  const pendingRef = useRef<Record<string, Record<string, CellVal>>>({});
  const inFlightRef = useRef<Set<string>>(new Set());
  const needsResaveRef = useRef<Set<string>>(new Set());
  const readingIdRef = useRef<Record<string, string>>({});
  const timersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const failStreakRef = useRef<Record<string, number>>({});
  const closedRef = useRef(false);
  const goneIdsRef = useRef<Set<string>>(new Set());
  const onRejectedRef = useRef(onRejected);
  useEffect(() => {
    onRejectedRef.current = onRejected;
  });

  const display = useCallback(
    (slot: ReadingSlot, paramKey: string): CellVal => {
      const col = dbColumnFor(paramKey);
      const k = cellKey(slot, col);
      if (k in edits) return edits[k];
      return dayReadings[slot]?.values[col] ?? null;
    },
    [edits, dayReadings]
  );

  const flush = useCallback(
    async (slot: ReadingSlot) => {
      if (inFlightRef.current.has(slot)) {
        needsResaveRef.current.add(slot);
        return;
      }
      const snapshot = pendingRef.current[slot];
      if (!snapshot || Object.keys(snapshot).length === 0) return;
      pendingRef.current[slot] = {};
      inFlightRef.current.add(slot);
      setSlotQueued((s) => ({ ...s, [slot]: false }));
      setSlotSaving((s) => ({ ...s, [slot]: true }));

      // false = nothing was sent: there was no row and no value to create one with.
      const send = async (payload: Record<string, CellVal>): Promise<boolean> => {
        const known = readingIdRef.current[slot] ?? dayReadings[slot]?.id;
        const rid = known && !goneIdsRef.current.has(known) ? known : undefined;
        if (rid) {
          try {
            await api.put(`/api/v1/farms/${farmId}/readings/${rid}`, toWirePayload(payload));
            return true;
          } catch (err) {
            // The row was deleted while this page was open: fall through to POST.
            if (!(err instanceof ApiError) || err.status !== 404) throw err;
            goneIdsRef.current.add(rid);
            delete readingIdRef.current[slot];
          }
        }
        // An all-blank create would leave a row that reads as logged and inflates the day's counts.
        if (Object.values(payload).every(isBlankVal)) return false;
        const create = async () => {
          const resp = await api.post<{ data: { id: string } }>(
            `/api/v1/farms/${farmId}/readings`,
            { cycle_id: cycleId, reading_date: date, reading_slot: slot, ...toWirePayload(payload) }
          );
          if (resp?.data?.id) readingIdRef.current[slot] = resp.data.id;
        };
        try {
          await create();
        } catch (err) {
          // Farm Tech and Lab Tech share one row per slot: someone else created it
          // after this page loaded, so update their row instead of re-POSTing forever.
          if (!(err instanceof ApiError) || err.status !== 409) throw err;
          const existing = await api.get<{ data: { id: string; reading_slot: string }[] }>(
            `/api/v1/farms/${farmId}/readings`,
            { params: { cycle_id: cycleId, start_date: date, end_date: date, limit: '100' } }
          );
          const id = existing.data?.find((r) => r.reading_slot === slot)?.id;
          if (!id) {
            // Their row was deleted between our POST and this GET: the slot is free again.
            await create();
            return true;
          }
          readingIdRef.current[slot] = id;
          await api.put(`/api/v1/farms/${farmId}/readings/${id}`, toWirePayload(payload));
        }
        return true;
      };
      const markSaved = (cols: string[], sent: boolean) => {
        const drop = (prev: Set<string>) => {
          const n = new Set(prev);
          cols.forEach((c) => n.delete(cellKey(slot, c)));
          return n;
        };
        setRejected(drop);
        setFailed(drop);
        failStreakRef.current[slot] = 0;
        if (Object.values(failStreakRef.current).every((n) => n === 0)) toast.dismiss(RETRY_TOAST_ID);
        if (!sent) return;
        setSavedCount((c) => c + 1);
        setSignedOut(false);
        toast.dismiss(SIGNED_OUT_TOAST_ID);
      };
      const requeue = (payload: Record<string, CellVal>, err: unknown) => {
        pendingRef.current[slot] = { ...payload, ...(pendingRef.current[slot] ?? {}) };
        if (isSignedOut(err)) {
          setSignedOut(true);
          // The buffered values live in this page only: signing in navigates away and drops them.
          toast.error('Signed out. Values not saved yet will be lost. Sign in again.', {
            id: SIGNED_OUT_TOAST_ID,
            duration: SIGNED_OUT_TOAST_MS,
          });
        } else if (isForbidden(err)) {
          toast.error('Not allowed to save this', { id: FORBIDDEN_TOAST_ID, duration: SIGNED_OUT_TOAST_MS });
        } else {
          failStreakRef.current[slot] = (failStreakRef.current[slot] ?? 0) + 1;
          if (retry === 'auto' || closedRef.current) {
            toast.error('Could not save — will retry', { id: RETRY_TOAST_ID, duration: RETRY_TOAST_MS });
          }
        }
        if (retry === 'manual') {
          setFailed((prev) => {
            const n = new Set(prev);
            Object.keys(payload).forEach((c) => n.add(cellKey(slot, c)));
            return n;
          });
        }
      };

      let authFailed = false;
      try {
        // Each pass drops what the server refused, so the payload only ever shrinks.
        let payload = snapshot;
        while (Object.keys(payload).length > 0) {
          try {
            markSaved(Object.keys(payload), await send(payload));
            break;
          } catch (err) {
            // A dead or foreign session can't succeed on retry; retrying would cross into the next login.
            authFailed = isSignedOut(err) || isForbidden(err);
            const named = [...getHardLimitErrorFields(err), ...getValidationErrorFields(err)].filter((c) => c in payload);
            // The server answered a 4xx that names no column of this payload (a retry repeats it): refuse them all.
            const unnamed = err instanceof ApiError && (err.status === 400 || err.status === 422);
            const refusedCols = named.length > 0 ? named : unnamed ? Object.keys(payload) : [];
            const withheldCols = refusedCols
              .map(pairPartnerColumn)
              .filter((c): c is string => !!c && c in payload && !refusedCols.includes(c));
            if (refusedCols.length === 0) {
              requeue(payload, err);
              break;
            }
            failStreakRef.current[slot] = 0; // the server answered: connectivity is fine
            setRejected((prev) => {
              const n = new Set(prev);
              [...refusedCols, ...withheldCols].forEach((c) => n.add(cellKey(slot, c)));
              return n;
            });
            setFailed((prev) => {
              const n = new Set(prev);
              [...refusedCols, ...withheldCols].forEach((c) => n.delete(cellKey(slot, c)));
              return n;
            });
            const remaining: Record<string, CellVal> = {};
            const refused: Record<string, CellVal> = {};
            for (const [c, v] of Object.entries(payload)) {
              if (refusedCols.includes(c)) refused[c] = v;
              else if (!withheldCols.includes(c)) remaining[c] = v;
            }
            onRejectedRef.current?.(slot, refusedCols, refused);
            payload = remaining;
          }
        }
      } finally {
        inFlightRef.current.delete(slot);
        setSlotSaving((s) => ({ ...s, [slot]: false }));
        // Refreshing readings now would only 401/403 again and swap the screen for a load error,
        // taking the typed values with it.
        if (!authFailed) onSaved();
        const hasPending = Object.keys(pendingRef.current[slot] ?? {}).length > 0;
        const autoRetry = retry === 'auto' || closedRef.current;
        if (!authFailed && (needsResaveRef.current.has(slot) || (autoRetry && hasPending))) {
          needsResaveRef.current.delete(slot);
          const streak = failStreakRef.current[slot] ?? 0;
          const delay = streak > 0 ? RETRY_DELAYS_MS[Math.min(streak, RETRY_DELAYS_MS.length) - 1] : RESAVE_DELAY_MS;
          timersRef.current[slot] = setTimeout(() => flush(slot), delay);
        }
      }
    },
    [farmId, cycleId, date, dayReadings, onSaved, retry]
  );

  const scheduleFlush = useCallback(
    (slot: ReadingSlot) => {
      clearTimeout(timersRef.current[slot]);
      timersRef.current[slot] = setTimeout(() => flush(slot), 450);
    },
    [flush]
  );

  const commitCell = useCallback(
    (slot: ReadingSlot, paramKey: string, value: CellVal) => {
      const col = dbColumnFor(paramKey);
      setEdits((e) => ({ ...e, [cellKey(slot, col)]: value }));
      setSlotQueued((s) => ({ ...s, [slot]: true }));
      pendingRef.current[slot] = { ...(pendingRef.current[slot] ?? {}), [col]: value };
      scheduleFlush(slot);
    },
    [scheduleFlush]
  );

  const retrySlot = useCallback(
    (slot: ReadingSlot) => {
      clearTimeout(timersRef.current[slot]);
      flush(slot);
    },
    [flush]
  );

  // Flush everything on unmount + register for global nav flush.
  const flushAll = useCallback(async () => {
    await Promise.all(ALL_SLOTS.map((slot) => flush(slot)));
  }, [flush]);
  useEffect(() => {
    closedRef.current = false;
    return () => {
      closedRef.current = true;
    };
  }, []);
  useEffect(() => {
    const unregister = registerAutosaveFlusher(flushAll);
    return () => {
      unregister();
      flushAll();
    };
  }, [flushAll]);

  return { display, commitCell, retrySlot, rejected, failed, slotSaving, slotQueued, savedCount, signedOut };
}
